// Phones and tablets: a joystick on the left half to move, swiping on the right half to look,
// automatic jumping, and pause / restart buttons. Landscape only: holding the device upright
// pauses the game. The stick appears where the thumb touches down on its half of the screen.

import type { InputState } from './inputState';

/** A phone or tablet: touch is the main pointer and there's no mouse. */
export function isTouchDevice(): boolean {
  return (
    !!window.matchMedia?.('(pointer: coarse)').matches &&
    !window.matchMedia('(pointer: fine)').matches &&
    navigator.maxTouchPoints > 0
  );
}

export const isPortrait = () => window.innerHeight > window.innerWidth;

/** How far (px) the knob travels from the stick's centre. */
const RADIUS = 56;
/** Stick deflection that counts as holding a movement key. */
const MOVE_THRESHOLD = 0.35;
/** Swipe look at look speed 1: degrees of turn per CSS pixel the thumb moves. */
const LOOK_DEG_PER_PX = 0.25;
/**
 * Swipes are smoothed over about this long (s): touch events and frames don't arrive in step, so
 * applying each event as it comes makes the view judder. Short enough not to feel late.
 */
const LOOK_SMOOTH = 0.02;
/** Automatic jumping starts once you've run up to about full running speed (or are in the air). */
const RUNUP_SPEED = 230;

class Stick {
  /** -1..1 each; y down is positive. */
  x = 0;
  y = 0;
  private id: number | null = null;
  private cx = 0;
  private cy = 0;
  private readonly base: HTMLElement;
  private readonly knob: HTMLElement;

  constructor(
    private readonly zone: HTMLElement,
    private readonly changed: () => void,
  ) {
    this.base = zone.querySelector('.stickBase')!;
    this.knob = zone.querySelector('.stickKnob')!;
    zone.addEventListener('touchstart', (e) => this.start(e), { passive: false });
    zone.addEventListener('touchmove', (e) => this.move(e), { passive: false });
    zone.addEventListener('touchend', (e) => this.end(e), { passive: false });
    zone.addEventListener('touchcancel', (e) => this.end(e), { passive: false });
  }

  private start(e: TouchEvent) {
    e.preventDefault();
    if (this.id !== null) return;
    const t = e.changedTouches[0];
    this.id = t.identifier;
    const r = this.zone.getBoundingClientRect();
    this.cx = t.clientX - r.left;
    this.cy = t.clientY - r.top;
    this.base.style.left = this.knob.style.left = this.cx + 'px';
    this.base.style.top = this.knob.style.top = this.cy + 'px';
    this.zone.classList.add('active');
    this.set(0, 0);
  }

  private move(e: TouchEvent) {
    e.preventDefault();
    for (const t of e.changedTouches) {
      if (t.identifier !== this.id) continue;
      const r = this.zone.getBoundingClientRect();
      let dx = t.clientX - r.left - this.cx;
      let dy = t.clientY - r.top - this.cy;
      const len = Math.hypot(dx, dy);
      if (len > RADIUS) {
        dx *= RADIUS / len;
        dy *= RADIUS / len;
      }
      this.knob.style.left = this.cx + dx + 'px';
      this.knob.style.top = this.cy + dy + 'px';
      this.set(dx / RADIUS, dy / RADIUS);
    }
  }

  private end(e: TouchEvent) {
    for (const t of e.changedTouches) {
      if (t.identifier !== this.id) continue;
      e.preventDefault();
      this.reset();
    }
  }

  reset() {
    this.id = null;
    this.zone.classList.remove('active');
    // Back to the resting spot (CSS).
    for (const el of [this.base, this.knob]) el.style.left = el.style.top = '';
    this.set(0, 0);
  }

  private set(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.changed();
  }
}

/**
 * Swipe-to-look area: the view turns exactly as far as the thumb moves (like a mouse), so small
 * moves aim finely and big swipes turn fast, with no joystick ramp. A dot follows the thumb.
 */
class SwipeArea {
  /** Turn waiting to be applied, CSS pixels (x right, y down). */
  dx = 0;
  dy = 0;
  private id: number | null = null;
  private lx = 0;
  private ly = 0;
  private readonly dot: HTMLElement;

  constructor(private readonly zone: HTMLElement) {
    this.dot = zone.querySelector('.stickKnob')!;
    zone.addEventListener('touchstart', (e) => this.start(e), { passive: false });
    zone.addEventListener('touchmove', (e) => this.move(e), { passive: false });
    zone.addEventListener('touchend', (e) => this.end(e), { passive: false });
    zone.addEventListener('touchcancel', (e) => this.end(e), { passive: false });
  }

  private place(t: Touch) {
    const r = this.zone.getBoundingClientRect();
    this.dot.style.left = t.clientX - r.left + 'px';
    this.dot.style.top = t.clientY - r.top + 'px';
  }

  private start(e: TouchEvent) {
    e.preventDefault();
    if (this.id !== null) return;
    const t = e.changedTouches[0];
    this.id = t.identifier;
    this.lx = t.clientX;
    this.ly = t.clientY;
    this.place(t);
    this.zone.classList.add('active');
  }

  private move(e: TouchEvent) {
    e.preventDefault();
    for (const t of e.changedTouches) {
      if (t.identifier !== this.id) continue;
      this.dx += t.clientX - this.lx;
      this.dy += t.clientY - this.ly;
      this.lx = t.clientX;
      this.ly = t.clientY;
      this.place(t);
    }
  }

  private end(e: TouchEvent) {
    for (const t of e.changedTouches) {
      if (t.identifier !== this.id) continue;
      e.preventDefault();
      this.id = null;
      this.zone.classList.remove('active');
    }
  }

  reset() {
    this.id = null;
    this.dx = this.dy = 0;
    this.zone.classList.remove('active');
  }
}

export class TouchControls {
  private readonly root = document.getElementById('touchUi')!;
  private readonly moveStick: Stick;
  private readonly lookArea: SwipeArea;
  /** Look speed (Settings): 1 = 0.25 degrees per pixel swiped. */
  lookSpeed = 1;
  private active = false;
  onPause: () => void = () => {};
  onRestart: () => void = () => {};

  constructor(private readonly input: InputState) {
    this.moveStick = new Stick(document.getElementById('stickMove')!, () => this.applyMove());
    this.lookArea = new SwipeArea(document.getElementById('stickLook')!);
    const tap = (id: string, fn: () => void) => {
      const el = document.getElementById(id)!;
      el.addEventListener('touchstart', (e) => {
        e.preventDefault();
        fn();
      });
      el.addEventListener('click', fn);
    };
    tap('touchPause', () => this.onPause());
    tap('touchRestart', () => this.onRestart());
    const jump = document.getElementById('touchJump')!;
    jump.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.jumpHeld = true;
    });
    const up = (e: Event) => {
      e.preventDefault();
      this.jumpHeld = false;
    };
    jump.addEventListener('touchend', up);
    jump.addEventListener('touchcancel', up);
  }

  /** Show the controls and start auto jumping (playing), or hide them (menu). */
  setActive(on: boolean) {
    this.active = on;
    this.jumpHeld = false;
    if (!on) this.input.setVirtual('jump', false);
    this.root.hidden = !on;
    this.moveStick.reset();
    this.lookArea.reset();
    this.applyMove();
  }

  private applyMove() {
    const { x, y } = this.moveStick;
    const i = this.input;
    i.setVirtual('forward', this.active && y < -MOVE_THRESHOLD);
    i.setVirtual('back', this.active && y > MOVE_THRESHOLD);
    i.setVirtual('moveleft', this.active && x < -MOVE_THRESHOLD);
    i.setVirtual('moveright', this.active && x > MOVE_THRESHOLD);
  }

  /** Strafe assist on (Settings): in the air, turning presses the matching strafe key. */
  assist = true;

  /**
   * Strafe assist, once per frame: while in the air and turning, hold the strafe key on the side
   * you turn to (left when turning left), and let go of forward/back. That is how air strafing
   * gains speed; on a phone it's nearly impossible by hand. Otherwise the move stick decides.
   */
  strafeAssist(turned: number, inAir: boolean, speed: number) {
    if (!this.active) return;
    // Jumping is automatic once you have run-up speed (or are already hopping): starting to hop
    // from a standstill never builds speed. The Jump button jumps any time.
    this.input.setVirtual('jump', inAir || speed >= RUNUP_SPEED || this.jumpHeld);
    if (!this.assist || !inAir || Math.abs(turned) < 1e-5) {
      this.applyMove();
      return;
    }
    const i = this.input;
    i.setVirtual('forward', false);
    i.setVirtual('back', false);
    i.setVirtual('moveleft', turned > 0);
    i.setVirtual('moveright', turned < 0);
  }

  /** The Jump button is held. */
  private jumpHeld = false;

  /**
   * Turn the view by this frame's share of the swipe (`dt` seconds). Returns how far it turned
   * to the right (positive) or left, in pixels, 0 when still.
   */
  look(view: { yaw: number; pitch: number }, dt: number, maxPitch: number) {
    const a = this.lookArea;
    if (!a.dx && !a.dy) return 0;
    const share = 1 - Math.exp(-dt / LOOK_SMOOTH);
    // The last scraps go at once, so turning (and the strafe assist) stops cleanly.
    const take = (v: number) => (Math.abs(v) < 0.05 ? v : v * share);
    const tx = take(a.dx);
    const ty = take(a.dy);
    a.dx -= tx;
    a.dy -= ty;
    const k = ((LOOK_DEG_PER_PX * Math.PI) / 180) * this.lookSpeed;
    view.yaw -= tx * k;
    view.pitch = Math.max(-maxPitch, Math.min(maxPitch, view.pitch + ty * k));
    return tx;
  }
}
